import React, { useMemo } from 'react'
import { STREAM_PULSE_CSS } from '../styles'
import {
  deliverySevFromMetrics,
  segmentSevForNode,
  sevStroke,
  type WorldSegment,
} from '../segments'
import { ClientGlyph, EdgeGlyph, StudioGlyph, type ClientGrain } from '../glyphs'
import { hopKey, type PosMap } from '../hopPlacement'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'
import type { WorldPoint } from '@/analysis/apiTypes'
import { useStore } from '@/store'

interface Props {
  hops: WorldFlowHop[]
  /** Map-space positions from shared `buildHopPositions` (same map arcs use). */
  positions: PosMap
  /** Skip these hop ids (`type:value`) — drawn as cluster badges instead. */
  hiddenHopIds?: Set<string>
  showStudios: boolean
  showClients: boolean
  showEdges?: boolean
  pan: { x: number; y: number }
  zoom: number
  pulseEpoch: number
  segment: WorldSegment
  clientGrain: ClientGrain
  onHoverEnter: (p: WorldPoint, x: number, y: number) => void
  onHoverLeave: (p: WorldPoint) => void
}

function hopToPoint(h: WorldFlowHop, sev: 0 | 1 | 2 | null): WorldPoint {
  return {
    id: `${h.type}:${h.value}`,
    label: h.label,
    lat: h.lat ?? 0,
    lon: h.lon ?? 0,
    total: h.totalStreams,
    crit: Number(h.criticalCount ?? 0),
    warn: Number(h.warningCount ?? 0),
    sev: sev ?? 2,
    usersTotal: h.usersTotal,
    country: h.country ?? undefined,
    studio: h.type === 'studio' ? h.value : undefined,
    cdnEdge: h.type === 'cdn_edge' ? h.value : undefined,
    avgSeverity: h.avgSeverity,
    worstMetric: h.worstMetric,
    metrics: h.metrics,
    distinctIps: h.distinctIps,
  } as WorldPoint
}

function toScreen(
  pos: { x: number; y: number },
  pan: { x: number; y: number },
  zoom: number,
) {
  return { x: pan.x + pos.x * zoom, y: pan.y + pos.y * zoom }
}

/** Tier 0 = critical, 1 = warning, 2 = healthy — matches HexMarker / sevStroke. */
function sevFill(sev: 0 | 1 | 2 | null, inactive: boolean): string {
  if (inactive || sev == null) return 'rgba(180,190,210,0.85)'
  if (sev === 0) return 'url(#sev-crit)'
  if (sev === 1) return 'url(#sev-warn)'
  return 'url(#sev-ok)'
}

function stressed(sev: 0 | 1 | 2 | null): boolean {
  return sev === 0 || sev === 1
}

function truncateLabel(s: string, max = 14): string {
  const t = s.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1)}…`
}

function clientMapLabel(h: WorldFlowHop, grain: ClientGrain): string {
  const preferred = String(h.label || h.value || '').trim()
  if (grain === 'continent') return truncateLabel(preferred)
  if (grain === 'country') {
    const v = String(h.value || '').trim()
    if (/^[A-Za-z]{2}$/.test(v)) return v.toUpperCase()
    if (h.country && /^[A-Za-z]{2}$/.test(h.country)) return h.country.toUpperCase()
    return truncateLabel(preferred)
  }
  // city — prefer City from City|CC
  const parts = String(h.value || '').split('|')
  const city = (parts[0] || preferred).trim()
  return truncateLabel(city)
}

function studioMapLabel(h: WorldFlowHop): string {
  return truncateLabel(String(h.label || h.value || '').trim())
}

export function HopMarkers({
  hops, positions, hiddenHopIds, showStudios, showClients, showEdges = false,
  pan, zoom, pulseEpoch, segment, clientGrain,
  onHoverEnter, onHoverLeave,
}: Props) {
  const descendToStreams = useStore((s) => s.descendToStreams)
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const setClientGeoFilter = useStore((s) => s.setClientGeoFilter)
  const setDimension = useStore((s) => s.setDimension)
  const setView = useStore((s) => s.setView)

  const studios = useMemo(() => hops.filter((h) => h.type === 'studio'), [hops])
  const cities = useMemo(() => hops.filter((h) => h.type === 'city'), [hops])
  const edges = useMemo(() => hops.filter((h) => h.type === 'cdn_edge'), [hops])
  const hidden = hiddenHopIds ?? new Set<string>()

  return (
    <g key={`hops-${pulseEpoch}-${clientGrain}`}>
      <style>{STREAM_PULSE_CSS}</style>

      {showStudios && studios.map((h) => {
        const id = hopKey('studio', h.value)
        if (hidden.has(id)) return null
        const pos = positions[id]
        if (!pos) return null
        const { x, y } = toScreen(pos, pan, zoom)
        const sev = segment === 'overall'
          ? deliverySevFromMetrics(h.metrics)
          : segmentSevForNode(h, segment)
        const color = sevStroke(sev)
        const inactive = h.totalStreams === 0
        const muted = h.lat == null || h.lon == null
        const point = hopToPoint(h, sev)
        const r = 18
        const fill = sevFill(sev, inactive || muted)

        return (
          <g
            key={`studio:${h.value}`}
            style={{ cursor: inactive ? 'default' : 'pointer' }}
            onClick={() => {
              if (inactive) return
              descendToStreams('studio', h.value)
              setView('hex')
            }}
            onPointerEnter={() => onHoverEnter(point, x, y)}
            onPointerLeave={() => onHoverLeave(point)}
          >
            {!inactive && stressed(sev) && (
              <circle className="stream-pulse" cx={x} cy={y} r={22} fill={color} fillOpacity={0.35} />
            )}
            <StudioGlyph
              cx={x}
              cy={y}
              r={r}
              fill={fill}
              fillOpacity={inactive ? 0.1 : muted ? 0.35 : 0.92}
              stroke={color}
              strokeWidth={1.4}
            />
            <text x={x} y={y + r + 12} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={9} fontWeight={700}>
              {studioMapLabel(h)}
            </text>
          </g>
        )
      })}

      {showEdges && edges.map((h) => {
        const id = hopKey('cdn_edge', h.value)
        if (hidden.has(id)) return null
        const pos = positions[id]
        if (!pos) return null
        const { x, y } = toScreen(pos, pan, zoom)
        const sev = segment === 'overall'
          ? deliverySevFromMetrics(h.metrics)
          : segmentSevForNode(h, segment)
        const color = sevStroke(sev)
        const inactive = h.totalStreams === 0
        const point = hopToPoint(h, sev)
        const fill = sevFill(sev, inactive)

        return (
          <g
            key={`edge:${h.value}`}
            style={{ cursor: inactive ? 'default' : 'pointer' }}
            onClick={() => {
              if (inactive) return
              setFilterOnly('cdnEdge', h.value)
              setDimension('stream')
              setView('hex')
            }}
            onPointerEnter={() => onHoverEnter(point, x, y)}
            onPointerLeave={() => onHoverLeave(point)}
          >
            {!inactive && stressed(sev) && (
              <circle className="stream-pulse" cx={x} cy={y} r={14} fill={color} fillOpacity={0.25} />
            )}
            <EdgeGlyph
              cx={x}
              cy={y}
              r={10}
              fill={fill}
              fillOpacity={inactive ? 0.15 : 0.95}
              stroke={inactive ? 'rgba(180,190,210,0.85)' : color}
              strokeWidth={1}
            />
          </g>
        )
      })}

      {showClients && cities.map((h) => {
        const id = hopKey('city', h.value)
        if (hidden.has(id)) return null
        const pos = positions[id]
        if (!pos) return null
        const { x, y } = toScreen(pos, pan, zoom)
        const sev = segmentSevForNode(h, segment)
        const color = sevStroke(sev)
        const inactive = h.totalStreams === 0
        const point = hopToPoint(h, sev)
        const r =
          clientGrain === 'continent' ? 18
          : clientGrain === 'country' ? 17
          : 15
        const pulseR = r + 3
        const fill = sevFill(sev, inactive)

        return (
          <g
            key={`city:${h.value}`}
            style={{ cursor: inactive ? 'default' : 'pointer' }}
            onClick={() => {
              if (inactive) return
              // Match WorldDetailTable: grain decides geo filter key.
              // Continent hops use values like "Europe" / "EU" — never as city.
              const parts = h.value.split('|')
              if (clientGrain === 'continent') {
                setClientGeoFilter({ grain: 'continent', continent: h.value })
              } else if (
                clientGrain === 'country' ||
                (parts.length !== 2 && (h.country || /^[A-Za-z]{2}$/.test(h.value)))
              ) {
                setClientGeoFilter({
                  grain: 'country',
                  country: (h.country ?? h.value).toUpperCase(),
                })
              } else if (parts.length === 2) {
                setClientGeoFilter({ grain: 'city', city: parts[0]!, country: parts[1]! })
              } else {
                setClientGeoFilter({ grain: 'city', city: h.value })
              }
              setDimension('stream')
              setView('hex')
            }}
            onPointerEnter={() => onHoverEnter(point, x, y)}
            onPointerLeave={() => onHoverLeave(point)}
          >
            {!inactive && stressed(sev) && (
              <circle className="stream-pulse" cx={x} cy={y} r={pulseR} fill={color} fillOpacity={0.25} />
            )}
            <ClientGlyph
              cx={x}
              cy={y}
              r={r}
              grain={clientGrain}
              fill={fill}
              fillOpacity={inactive ? 0.1 : 0.92}
              stroke={inactive ? 'rgba(180,190,210,0.85)' : color}
              strokeWidth={1.8}
            />
            <text x={x} y={y + r + 12} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={9} fontWeight={700}>
              {clientMapLabel(h, clientGrain)}
            </text>
          </g>
        )
      })}
    </g>
  )
}
